import { useSources } from '#pplx/frontend/apps/ask/sources/hooks/useSources.js';
import { isSourceOmitted } from '#pplx/frontend/apps/ask/sources/types/omittedSources.js';
import { MAX_SOURCE_ACTIVITY_ENTRIES, useSourceActivity, } from '#pplx/frontend/apps/ask/user-settings/hooks/useSourceActivity.js';
import { useCallback, useMemo, useState } from 'react';
const DEFAULT_SOURCE_SUGGESTIONS = ['scholar', 'gcal', 'google_drive'];
export const useSuggestedSources = ({ enabled, omittedSources, defaultSuggestions = DEFAULT_SOURCE_SUGGESTIONS, count = MAX_SOURCE_ACTIVITY_ENTRIES, productSurface, }) => {
    const { activity } = useSourceActivity();
    const [frozenActivity, setFrozenActivity] = useState(activity);
    // Reuses the canonical composer source query instead of issuing an
    // independent /rest/sources baseline with a divergent request shape.
    const { sources, getSource } = useSources({ enabled, productSurface });
    // Intentionally derived from the allowed-filtered list: a member whose
    // family primary is not allowed should not normalize to (and thereby
    // suggest) that unusable primary; the unresolved member is dropped by the
    // omission filter below, which matches what the menu would render anyway.
    const familyPrimaryByMemberId = useMemo(() => {
        return new Map(sources.flatMap((sourceId) => {
            const source = getSource(sourceId);
            return (source?.family_info?.members ?? []).map((member) => [member.id, sourceId]);
        }));
    }, [sources, getSource]);
    const suggested = useMemo(() => {
        // web is always suggested
        const suggested = new Set(['web']);
        // Add sources based on activity first
        frozenActivity
            .sort((a, b) => b.lastUsedAt - a.lastUsedAt)
            .map((activity) => activity.sourceType)
            .forEach((sourceType) => suggested.add(sourceType));
        // Then add default suggestions
        defaultSuggestions.forEach((source) => suggested.add(source));
        // Finally, return the top `count` suggestions that are not omitted
        return Array.from(new Set(Array.from(suggested).map((sourceType) => {
            return getSource(sourceType) !== undefined
                ? sourceType
                : (familyPrimaryByMemberId.get(sourceType) ?? sourceType);
        })))
            .filter((sourceType) => !isSourceOmitted(getSource(sourceType), omittedSources))
            .slice(0, count);
    }, [
        frozenActivity,
        count,
        omittedSources,
        defaultSuggestions,
        getSource,
        familyPrimaryByMemberId,
    ]);
    const refresh = useCallback(() => {
        setFrozenActivity(activity);
    }, [activity]);
    return useMemo(() => ({
        suggested,
        refresh,
    }), [suggested, refresh]);
};
//# sourceMappingURL=useSuggestedSources.js.map